import React, { useEffect, useState, ReactNode } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { ProgressBarProps, GetStrengthFunction } from './ProgressBar.types'
import { useConfig } from 'msp-integrations'
import Icons from '@atoms/Icons/Icons'
import { twMerge } from 'tailwind-merge'

const getStrength: GetStrengthFunction = (value, TARGET_BREW_BOXES) => {
  return Math.min((value / TARGET_BREW_BOXES) * 100, 100)
}

const ProgressBar = ({
  basketTotalPrice,
  isFreeShipping,
  minAmountFreeShipping,
  targetBrewBoxes,
  quantityOfBrews = 0
}: ProgressBarProps) => {
  const {
    theme: {
      mspComponents: { progressBar }
    }
  } = useConfig()
  const [cartStrength, setCartStrength] = useState<number>(0)
  const bgColor = progressBar?.containerBgColor
  const barColor = progressBar?.barColor

  const brewsLeft = targetBrewBoxes - quantityOfBrews
  const intl = useIntl()
  const mindest = intl.formatMessage({
    id: 'msp.cart.shipment.minimum.free.shipping.term',
    defaultMessage: 'Free shipping'
  })

  const getBrewBoxWord = (brewsLeft: number) => {
    const brewBoxWordSingle = intl.formatMessage({
      id: 'msp.cart.shipment.brewbox.single',
      defaultMessage: 'Tea box'
    })

    const brewBoxWordMultiple = intl.formatMessage({
      id: 'msp.cart.shipment.brewbox.multiple',
      defaultMessage: 'Tea boxes'
    })

    return brewsLeft === 1 ? brewBoxWordSingle : brewBoxWordMultiple
  }

  const brewBoxWord = getBrewBoxWord(brewsLeft)

  const renderMessage = () => {
    let values: Record<string, ReactNode>

    if (brewsLeft > 0 && basketTotalPrice < minAmountFreeShipping) {
      values = {
        quantity: <span className='font-bold'>{brewsLeft}</span>,
        brewBoxWord: brewBoxWord,
        mindest: <span className='font-bold uppercase'>{mindest}</span>
      }

      return (
        <FormattedMessage
          id='msp.cart.shipment.minimum.free.shipping'
          defaultMessage='Add {quantity} {brewBoxWord} to reach {mindest}'
          values={values}
        />
      )
    } else {
      return (
        <div className='flex items-start'>
          <span className='mr-2'>
            <FormattedMessage
              id='msp.cart.shipment.minimum.free.delivery'
              defaultMessage='Congratulations, you have reached {mindest}!'
              values={{
                mindest: <span className='font-bold uppercase'>{mindest}</span>
              }}
            />
          </span>
          <Icons iconName='Check' height='19px' width='17px' />
        </div>
      )
    }
  }

  useEffect(() => {
    const strength = getStrength(quantityOfBrews, targetBrewBoxes)
    setCartStrength(strength)
  }, [quantityOfBrews])

  return (
    <div className={(twMerge('flex items-start'), bgColor)}>
      <div className='w-full px-5 py-4'>
        <div className='bg-machine2-75 h-2'>
          <div
            className={twMerge('h-full transition-all duration-150', barColor)}
            style={{
              width:
                basketTotalPrice > minAmountFreeShipping
                  ? '100%'
                  : `${cartStrength}%`
            }}
          />
        </div>
        <p className='text-14 mt-2'>{renderMessage()}</p>
      </div>
    </div>
  )
}

export default ProgressBar
